import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Languages, Settings, BarChart3, Edit3, Check, X, 
  Loader2, AlertCircle, RefreshCw, ArrowLeft, Search,
  Globe, CheckCircle2, Sparkles, Copy, ClipboardCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Supported languages
const LANGUAGES = [
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
  { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ar', name: 'Arabic', flag: '🇦🇪' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'ms', name: 'Malay', flag: '🇲🇾' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' }
];

export default function AdminTranslationManager() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('verify');
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState({ service: 'deepl' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Verification form state
  const [verifyForm, setVerifyForm] = useState({
    original_text: '',
    current_translation: '',
    target_language: 'vi',
    industry_context: ''
  });
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Apply correction state
  const [applyForm, setApplyForm] = useState({
    profile_id: '',
    field_path: ''
  });
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/translation/usage`, {
        withCredentials: true
      });
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const updateVerificationService = async (service) => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/settings/translation-service`, 
        { service },
        { withCredentials: true }
      );
      setSettings({ service });
      setSuccess(`Verification service changed to ${service.toUpperCase()}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    setVerifyResult(null);
    setCopied(false);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/translation/verify`, 
        verifyForm,
        { withCredentials: true }
      );
      setVerifyResult(response.data);
      fetchUsage(); // Refresh usage stats
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleApplyCorrection = async () => {
    if (!applyForm.profile_id || !applyForm.field_path) {
      setError('Please enter Profile ID/URL and Field Path');
      return;
    }
    
    if (!verifyResult?.verified_translation) {
      setError('No verified translation available. Please verify a translation first.');
      return;
    }
    
    // Extract profile ID from URL if a full URL was pasted
    let profileId = applyForm.profile_id.trim();
    if (profileId.includes('/')) {
      // Extract ID from URL patterns like /profile/edit/{id} or /profiles/{id}/edit
      const urlMatch = profileId.match(/(?:profile|profiles)\/(?:edit\/)?([a-f0-9-]+)/i);
      if (urlMatch) {
        profileId = urlMatch[1];
      } else {
        // Try to get the last path segment that looks like a UUID
        const segments = profileId.split('/').filter(s => s);
        const uuidSegment = segments.find(s => /^[a-f0-9-]{36}$/i.test(s));
        if (uuidSegment) {
          profileId = uuidSegment;
        }
      }
    }
    
    setApplying(true);
    setError('');
    
    try {
      await axios.put(
        `${API_URL}/api/profiles/${profileId}/translations/${verifyForm.target_language}/edit`,
        {
          field_path: applyForm.field_path,
          translated_text: verifyResult.verified_translation
        },
        { withCredentials: true }
      );
      setSuccess(`✓ Translation saved! Refresh the profile page to see the update.`);
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      console.error('Apply correction error:', err.response?.data || err);
      setError(err.response?.data?.detail || 'Failed to apply correction. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const usagePercentage = usage?.deepl_free_tier?.percentage_used || 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Languages className="w-5 h-5 text-[#C41E3A]" />
                  Translation Manager
                </h1>
                <p className="text-zinc-500 text-sm">Edit translations & verify with DeepL/Gemini</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('verify')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'verify' 
                ? 'border-[#C41E3A] text-white' 
                : 'border-transparent text-zinc-500 hover:text-white'
            }`}
          >
            <Search className="w-4 h-4 inline mr-2" />
            Verify Translation
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'usage' 
                ? 'border-[#C41E3A] text-white' 
                : 'border-transparent text-zinc-500 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Usage Stats
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'settings' 
                ? 'border-[#C41E3A] text-white' 
                : 'border-transparent text-zinc-500 hover:text-white'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-900/20 border border-green-800 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Verify Tab */}
        {activeTab === 'verify' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Verify Form */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#C41E3A]" />
                Verify Translation Quality
              </h2>
              <p className="text-zinc-400 text-sm mb-6">
                Paste a suspicious translation to verify it using DeepL or Gemini. 
                This helps spot-check machine translations for technical accuracy.
              </p>
              
              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Original English Text</label>
                  <textarea
                    value={verifyForm.original_text}
                    onChange={(e) => setVerifyForm({...verifyForm, original_text: e.target.value})}
                    required
                    rows={3}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                    placeholder="e.g., waterproof seals and wiring harness"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Current Translation (to verify)</label>
                  <textarea
                    value={verifyForm.current_translation}
                    onChange={(e) => setVerifyForm({...verifyForm, current_translation: e.target.value})}
                    required
                    rows={3}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                    placeholder="e.g., con dấu chống thấm và dây nịt"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Target Language</label>
                    <select
                      value={verifyForm.target_language}
                      onChange={(e) => setVerifyForm({...verifyForm, target_language: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C41E3A]"
                    >
                      {LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Industry Context (Optional)</label>
                    <input
                      type="text"
                      value={verifyForm.industry_context}
                      onChange={(e) => setVerifyForm({...verifyForm, industry_context: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                      placeholder="e.g., automotive components"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={verifying || !verifyForm.original_text || !verifyForm.current_translation}
                  className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Verify with {settings.service === 'gemini' ? 'Gemini' : 'DeepL'}
                    </>
                  )}
                </button>
              </form>
            </div>
            
            {/* Verification Result */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Verification Result
              </h2>
              
              {!verifyResult ? (
                <div className="text-center py-12 text-zinc-500">
                  <Languages className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Submit a translation to verify</p>
                  <p className="text-sm mt-2">Results will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase tracking-wide mb-1">Original</label>
                    <p className="text-zinc-300 bg-zinc-800 rounded-lg px-4 py-3">{verifyResult.original_text}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase tracking-wide mb-1">Current Translation</label>
                    <p className="text-yellow-400 bg-zinc-800 rounded-lg px-4 py-3">{verifyResult.current_translation}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-zinc-500 uppercase tracking-wide mb-1">
                      Verified by {verifyResult.service_used?.toUpperCase()}
                    </label>
                    <p className="text-green-400 bg-zinc-800 rounded-lg px-4 py-3 border border-green-800">
                      {verifyResult.verified_translation}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-zinc-500 pt-4 border-t border-zinc-800">
                    <span>Characters used: {verifyResult.characters_used}</span>
                    <span>Service: {verifyResult.service_used}</span>
                  </div>
                  
                  {verifyResult.current_translation !== verifyResult.verified_translation && (
                    <div className="space-y-4">
                      <div className="bg-yellow-900/20 border border-yellow-800 text-yellow-400 px-4 py-3 rounded-lg text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        Translations differ - apply the correction below
                      </div>
                      
                      {/* Copy Button */}
                      <button
                        onClick={() => copyToClipboard(verifyResult.verified_translation)}
                        className="w-full bg-zinc-800 hover:bg-zinc-700 text-white py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {copied ? (
                          <>
                            <ClipboardCheck className="w-4 h-4 text-green-400" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Verified Translation
                          </>
                        )}
                      </button>
                      
                      {/* Apply to Profile Form */}
                      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-zinc-400 font-medium">Apply to Profile:</p>
                        <input
                          type="text"
                          value={applyForm.profile_id}
                          onChange={(e) => setApplyForm({...applyForm, profile_id: e.target.value})}
                          placeholder="Profile ID (from admin dashboard)"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                        />
                        <input
                          type="text"
                          value={applyForm.field_path}
                          onChange={(e) => setApplyForm({...applyForm, field_path: e.target.value})}
                          placeholder="Field path (e.g., tagline, profile_sections.who_we_are)"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                        />
                        <button
                          onClick={handleApplyCorrection}
                          disabled={applying || !applyForm.profile_id || !applyForm.field_path}
                          className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {applying ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Applying...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Apply Correction
                            </>
                          )}
                        </button>
                        <p className="text-xs text-zinc-500">
                          Common fields: tagline, profile_sections.who_we_are, profile_sections.what_we_do, key_contact.bio_text
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* DeepL Usage Card */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">DeepL Free Tier</h3>
                <button onClick={fetchUsage} className="text-zinc-500 hover:text-white">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Characters Used</span>
                  <span className="text-white">{usage?.deepl_free_tier?.used?.toLocaleString() || 0}</span>
                </div>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      usagePercentage > 80 ? 'bg-red-500' : 
                      usagePercentage > 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>{usagePercentage}% used</span>
                  <span>{usage?.deepl_free_tier?.remaining?.toLocaleString() || 500000} remaining</span>
                </div>
              </div>
              
              <p className="text-xs text-zinc-500">
                Monthly limit: 500,000 characters
              </p>
            </div>
            
            {/* Service Usage Breakdown */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h3 className="font-bold mb-4">Usage This Month</h3>
              
              {usage?.usage_by_service && Object.keys(usage.usage_by_service).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(usage.usage_by_service).map(([service, data]) => (
                    <div key={service} className="flex items-center justify-between">
                      <span className="text-zinc-400 capitalize">{service}</span>
                      <div className="text-right">
                        <span className="text-white font-medium">{data.requests} requests</span>
                        <span className="text-zinc-500 text-sm block">{data.characters?.toLocaleString()} chars</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No verification requests this month</p>
              )}
            </div>
            
            {/* Current Period */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h3 className="font-bold mb-4">Current Period</h3>
              <p className="text-3xl font-bold text-[#C41E3A]">{usage?.current_month || '-'}</p>
              <p className="text-zinc-500 text-sm mt-2">DeepL free tier resets monthly</p>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-xl">
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#C41E3A]" />
                Verification Service
              </h2>
              <p className="text-zinc-400 text-sm mb-6">
                Choose which service to use for translation verification. 
                Switch anytime based on quality or usage needs.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => updateVerificationService('deepl')}
                  disabled={loading}
                  className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                    settings.service === 'deepl' 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <span className="text-xl font-bold text-blue-400">D</span>
                    </div>
                    <div className="text-left">
                      <p className="font-medium">DeepL</p>
                      <p className="text-xs text-zinc-500">500K chars/month free tier</p>
                    </div>
                  </div>
                  {settings.service === 'deepl' && (
                    <Check className="w-5 h-5 text-[#C41E3A]" />
                  )}
                </button>
                
                <button
                  onClick={() => updateVerificationService('gemini')}
                  disabled={loading}
                  className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${
                    settings.service === 'gemini' 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Gemini</p>
                      <p className="text-xs text-zinc-500">Uses Emergent LLM credits • Better for technical terms</p>
                    </div>
                  </div>
                  {settings.service === 'gemini' && (
                    <Check className="w-5 h-5 text-[#C41E3A]" />
                  )}
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-sm text-zinc-400">
                  <strong className="text-white">Tip:</strong> Start with DeepL's free tier. 
                  If you need better technical terminology handling, switch to Gemini 
                  which can use industry context for more accurate translations.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
